/**
 * Auth context + hook + public types.
 *
 * Lives separately from AuthContext.tsx so that file exports only the
 * AuthProvider component — react-refresh/only-export-components requires
 * component files to not also export hooks / non-component values.
 */
import { createContext, useContext } from "react";

import type { components } from "@/api/schema";

type MeResponse = components["schemas"]["_MeResponseSchema"];

export type AuthUser = Omit<MeResponse, "expires_in" | "idle_expires_in" | "server_time">;
export type AuthMethod = AuthUser["auth_method"];

export type Status = "loading" | "authenticated" | "unauthenticated";

export type State = {
  status: Status;
  user: AuthUser | null;
  expires_at: number | null;
  idle_expires_at: number | null;
  server_time_offset: number;
};

export type AuthContextValue = State & {
  logout: () => Promise<void>;
  refresh_me: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
